import {
  type MediaCardType,
  mediaCardTypeStrings,
} from '@wandb/weave/common/types/media';

import {type MetricKeySearchResult} from '../../MetricSearch/useMetricKeySearch';
import {mediaCardTypeForMetric} from './mediaCardTypeForMetric';

/**
 * Whether a metric-search result should be offered as a media key: it has a
 * card type this surface renders, and that type matches the one already
 * selected, since a panel shows one card type at a time.
 *
 * Mirrors `isValidMediaKeyOption`, but reads its types off metric-search
 * results because NGW doesn't hold the key info in memory to look them up in.
 *
 * Uses the single source of truth for a media card type. Once
 * `collapseMetricTypes` uses `latestType` for media types, this will stop
 * arbitrarily checking the option's first media type to see if it matches the
 * overall media card type.
 *
 * A null `selectedCardType` is permissive rather than rejecting: it means the
 * panel could not resolve its own type, and a too-wide list beats an empty one.
 */
export function isValidMediaKeyOptionForMetric(
  metric: MetricKeySearchResult,
  selectedCardType: MediaCardType | null | undefined,
  supportedMediaTypes: MediaCardType[] = mediaCardTypeStrings
): boolean {
  const mediaCardType = mediaCardTypeForMetric(metric);
  if (mediaCardType == null) {
    return false;
  }

  if (!supportedMediaTypes.includes(mediaCardType)) {
    return false;
  }

  if (selectedCardType == null) {
    return true;
  }

  // Media panels render one card type at a time, so once a key is selected the
  // rest of the selection has to match it.
  return mediaCardType === selectedCardType;
}
